"use client";

import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import * as React from "react";

import { cn } from "../lib/utils";
import { Icon } from "./icon";
import { Label, LabelProps } from "./label";

interface CheckboxProps
  extends
    React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    Pick<LabelProps, "tooltipContent" | "tooltipIcon"> {
  checkboxContent?: React.ReactNode;
  value?: Exclude<
    React.ComponentProps<typeof CheckboxPrimitive.Root>["value"],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- lint-backlog: any is part of the current public type; narrowing it changes the API
    readonly any[] | any[]
  >;
}

/**
 * Radix UI (API reference): https://www.radix-ui.com/docs/primitives/components/checkbox#api-reference
 *
 * shadcn/ui docs: https://ui.shadcn.com/docs/components/checkbox
 *
 * _There are built-in form components for convenience that add a label, description, and error message._
 *
 * Checkbox with a label.
 *
 * @param checkboxContent - Text label part of the checkbox. You only need to pass string for most cases, but there is the option to pass a React node.
 * @param tooltipContent - Optional tooltip content to show on hover.
 * @param tooltipIcon - Optional custom icon for the tooltip.
 * @param props - The props for the checkbox primitive.
 */
const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(
  (
    { className, checkboxContent, tooltipContent, tooltipIcon, ...props },
    ref,
  ) => {
    props.name = props.name ?? props.value?.toString();

    const checkboxElement = (
      <CheckboxPrimitive.Root
        className={cn(
          "peer h-4 w-4 shrink-0 rounded-sm border border-primary leading-normal shadow focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
          className,
        )}
        ref={ref}
        {...props}
      >
        <CheckboxPrimitive.Indicator
          className={cn("flex items-center justify-center text-current")}
        >
          <Icon icon="check" size="xs" weight="bold" />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );

    const labelElement =
      typeof checkboxContent === "string" ||
      typeof checkboxContent === "number" ? (
        <Label
          htmlFor={props?.id}
          tooltipContent={tooltipContent}
          tooltipIcon={tooltipIcon}
        >
          {checkboxContent}
        </Label>
      ) : (
        checkboxContent
      );

    return (
      <div className={cn("flex items-center space-x-2", className)}>
        {checkboxElement}
        {labelElement}
      </div>
    );
  },
);

Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox, type CheckboxProps };
